/**
 * WeightingQuestionsEditor — the v2 `weighting_questions` list. Unlike
 * must-ask/cross-cutting questions (which reduce the candidate set or gate on
 * a system requirement), a weighting question never excludes a cell — it only
 * nudges scoring weights based on the answer. Each card is: slug + question +
 * options (reused OptionsEditor) + a `maps_to` table (metric → weight delta) +
 * a required explanation shown to the customer ("why we asked").
 */
import { moveItem, newMapsToRow, newWeightingQuestion } from './flowModel.js'
import { OptionsEditor } from './OptionsEditor.jsx'
import { RowControls } from './RowControls.jsx'
import { MetricPicker } from './MetricPicker.jsx'
import { FlowHelp } from './FlowHelp.jsx'

export function WeightingQuestionsEditor({ questions, onChange, metrics }) {
  const update = (i, patch) =>
    onChange(questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)))

  const updateMapsTo = (qi, mi, patch) => {
    const q = questions[qi]
    const mapsTo = (q.mapsTo || []).map((m, idx) => (idx === mi ? { ...m, ...patch } : m))
    update(qi, { mapsTo })
  }

  return (
    <section className="admin-aim-section admin-flow-section">
      <div className="admin-flow-section__head">
        <h3 className="admin-aim-section-title">
          Weighting questions <FlowHelp k="weightingQuestions" />
        </h3>
        <button
          type="button"
          className="admin-ai-btn admin-ai-btn--ghost admin-flow-add"
          onClick={() => onChange([...questions, newWeightingQuestion()])}
        >
          + Add weighting question
        </button>
      </div>
      <p className="admin-flow-hint">
        Never excludes a cell — the answer only nudges scoring weights. Each
        question needs at least one metric delta and an explanation the
        customer sees ("why we asked").
      </p>
      {questions.length === 0 && (
        <p className="admin-aim-empty admin-flow-empty">No weighting questions yet.</p>
      )}
      <ol className="admin-flow-list admin-flow-qlist" role="list">
        {questions.map((q, i) => (
          <li key={q.id} className="admin-flow-q-card">
            <div className="admin-flow-q-head">
              <span className="admin-flow-q-num" aria-hidden="true">
                {i + 1}
              </span>
              <input
                className="admin-aim-input admin-flow-q-slug"
                type="text"
                placeholder="slug (e.g. priority)"
                value={q.slug}
                aria-label={`Weighting question ${i + 1} slug`}
                onInput={(e) => update(i, { slug: e.currentTarget.value })}
              />
              {i === 0 && <FlowHelp k="questionSlug" />}
              <RowControls
                index={i}
                count={questions.length}
                label={`weighting question ${i + 1}`}
                onMove={(d) => onChange(moveItem(questions, i, d))}
                onRemove={() => onChange(questions.filter((_, idx) => idx !== i))}
              />
            </div>
            <textarea
              className="admin-aim-textarea admin-flow-q-text"
              rows={2}
              placeholder="Question shown to the user"
              value={q.question}
              aria-label={`Weighting question ${i + 1} text`}
              onInput={(e) => update(i, { question: e.currentTarget.value })}
            />
            <OptionsEditor
              options={q.options}
              onChange={(opts) => update(i, { options: opts })}
            />

            <div className="admin-flow-wq-mapsto">
              <div className="admin-flow-wq-mapsto__label">
                Maps to (metric → weight delta) {i === 0 && <FlowHelp k="mapsTo" />}
              </div>
              {(q.mapsTo || []).length === 0 && (
                <p className="admin-aim-empty admin-flow-empty">
                  No metric deltas yet — this question won't affect ranking until you add one.
                </p>
              )}
              <ul className="admin-flow-list" role="list">
                {(q.mapsTo || []).map((m, mi) => (
                  <li key={m.id} className="admin-flow-wq-mapsto-row">
                    <MetricPicker
                      value={m.metric}
                      metrics={metrics}
                      ariaLabel={`Weighting question ${i + 1} maps-to ${mi + 1} metric`}
                      onChange={(metric) => updateMapsTo(i, mi, { metric })}
                    />
                    <input
                      className="admin-aim-input admin-flow-num"
                      type="number"
                      step="any"
                      placeholder="delta"
                      value={m.delta}
                      aria-label={`Weighting question ${i + 1} maps-to ${mi + 1} delta`}
                      onInput={(e) => updateMapsTo(i, mi, { delta: e.currentTarget.value })}
                    />
                    <RowControls
                      index={mi}
                      count={(q.mapsTo || []).length}
                      label={`maps-to row ${mi + 1}`}
                      onRemove={() =>
                        update(i, { mapsTo: (q.mapsTo || []).filter((_, idx) => idx !== mi) })
                      }
                    />
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="admin-ai-btn admin-ai-btn--ghost admin-flow-add"
                onClick={() => update(i, { mapsTo: [...(q.mapsTo || []), newMapsToRow()] })}
              >
                + Add metric delta
              </button>
            </div>

            <textarea
              className="admin-aim-textarea admin-flow-q-text"
              rows={2}
              placeholder="Explanation shown to the customer (why we asked)"
              value={q.explanation}
              aria-label={`Weighting question ${i + 1} explanation`}
              onInput={(e) => update(i, { explanation: e.currentTarget.value })}
            />
          </li>
        ))}
      </ol>
    </section>
  )
}
